import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as XLSX from "xlsx";
import { downloadBlob } from "@/utils/download";
import {
  ShieldCheck, Clock, Calendar, AlertTriangle, Ban, Eye, Loader2,
} from "lucide-react";
import { StatCard } from "./StatCard";

const severityColors = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const actionLabels = {
  login_success: "Login OK",
  login_failed: "Login falhou",
  register: "Registro",
  password_change: "Senha alterada",
  settings_change: "Config. alterada",
  financial_create: "Fatura criada",
  financial_update: "Fatura editada",
  financial_delete: "Fatura eliminada",
  financial_pay: "Fatura paga",
  data_export: "Exportacao",
  token_generate: "Token gerado",
};

export function PlatformAuditTab({ adminAxios }) {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/platform-admin/audit/logs?start_date=${startDate}&end_date=${endDate}&limit=300`;
      if (filterAction && filterAction !== "all") url += `&action=${filterAction}`;
      if (filterSeverity && filterSeverity !== "all") url += `&severity=${filterSeverity}`;
      const [logsRes, statsRes] = await Promise.all([
        adminAxios.get(url),
        adminAxios.get("/platform-admin/audit/stats"),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  }, [adminAxios, filterAction, filterSeverity, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    const rows = logs.map((l) => ({
      Data: l.timestamp?.slice(0, 19)?.replace("T", " "),
      Restaurante: l.restaurant_id || "Plataforma",
      Ação: actionLabels[l.action] || l.action,
      Severidade: l.severity,
      Detalhes: l.details,
      IP: l.ip || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit");
    const buf = XLSX.write(wb, { bookType: "csv", type: "array" });
    downloadBlob(new Blob([buf], { type: "text/csv;charset=utf-8" }), `audit_platform_${endDate}.csv`);
    toast.success("CSV exportado!");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="platform-audit-tab">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total Registros" value={stats.total} icon={ShieldCheck} />
          <StatCard label="Últimas 24h" value={stats.last_24h} icon={Clock} color="blue" />
          <StatCard label="Últimos 7 dias" value={stats.last_7d} icon={Calendar} />
          <StatCard label="Avisos (7d)" value={stats.warnings_7d} icon={AlertTriangle} color="amber" />
          <StatCard label="Logins falhos (24h)" value={stats.login_failures_24h} icon={Ban} color="red" />
        </div>
      )}

      {stats?.top_actions?.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase mb-2">Ações mais frequentes (7 dias)</p>
            <div className="flex flex-wrap gap-2">
              {stats.top_actions.map((a) => (
                <Badge key={a.action} variant="outline" className="text-xs">
                  {actionLabels[a.action] || a.action}: {a.count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-2 items-end">
        <div className="flex-1 grid grid-cols-4 gap-2">
          <div>
            <Label className="text-xs">Ação</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="login">Logins</SelectItem>
                <SelectItem value="financial">Financeiro</SelectItem>
                <SelectItem value="data">Dados</SelectItem>
                <SelectItem value="settings">Configuracoes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Severidade</Label>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1 text-xs"
          onClick={exportCSV}
          data-testid="export-audit-csv"
        >
          <Eye className="w-3 h-3" /> CSV
        </Button>
      </div>

      <div className="bg-card rounded-lg border border-border/50 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/50 text-muted-foreground text-xs uppercase">
              <th className="text-left p-3 font-medium w-36">Data/Hora</th>
              <th className="text-left p-3 font-medium">Ação</th>
              <th className="text-left p-3 font-medium">Detalhes</th>
              <th className="text-center p-3 font-medium w-20">Nível</th>
              <th className="text-left p-3 font-medium w-28 hidden lg:table-cell">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/30">
            {logs.map((l) => (
              <tr key={l.id} className="hover:bg-muted/50">
                <td className="p-3 text-muted-foreground text-xs font-mono">
                  {l.timestamp
                    ? new Date(l.timestamp).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })
                    : "-"}
                </td>
                <td className="p-3"><span className="font-medium text-xs">{actionLabels[l.action] || l.action}</span></td>
                <td className="p-3 text-muted-foreground text-xs max-w-xs truncate">{l.details}</td>
                <td className="p-3 text-center">
                  <Badge className={`text-[10px] ${severityColors[l.severity] || "bg-gray-100 text-gray-700"}`}>{l.severity}</Badge>
                </td>
                <td className="p-3 text-muted-foreground text-xs font-mono hidden lg:table-cell">{l.ip || "-"}</td>
              </tr>
            ))}
            {logs.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhum registro</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">{logs.length} registros</p>
    </div>
  );
}
